/**
 * Область работы: пространство и бренд внутри активной организации.
 *
 * Три уровня спеки §5 — организация → пространство → бренд — распадаются на два
 * механизма, и путать их нельзя.
 *
 * **Организация живёт в токене.** Сменить её можно только через
 * `POST /auth/context`; ни один эндпоинт не принимает `organization_id`, потому
 * что выбор арендатора нельзя отдавать клиенту. Этим занимается `session.tsx`.
 *
 * **Пространство и бренд — это выбор на экране.** Они уезжают обычными
 * параметрами запроса (`GET /brands?workspace_id=…`) и изоляции не несут:
 * изоляцию держит база по токену. Поэтому их можно и нужно хранить на клиенте,
 * чтобы человек, вернувшись, попал туда же, где был.
 *
 * Хранение — `sessionStorage`, а не `localStorage`: выбор бренда живёт ровно
 * столько, сколько вкладка. Ключ включает организацию, иначе после переключения
 * арендатора кабинет попробует открыть чужой бренд и получит 404.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { api, type Page } from './api/client';
import { useSession } from './session';
import type { components } from './api/schema';

export type Workspace = components['schemas']['Workspace'];
export type Brand = components['schemas']['Brand'];

export type ScopeState =
  | { kind: 'loading' }
  | { kind: 'ready'; workspaces: Workspace[]; brands: Brand[] }
  | { kind: 'failed'; error: unknown };

interface ScopeApi {
  state: ScopeState;
  workspaceId: string | null;
  brandId: string | null;
  workspace: Workspace | null;
  brand: Brand | null;
  /**
   * Бренды, по которым продукт работает: все, кроме архивных.
   *
   * Это ЕДИНСТВЕННЫЙ список для переключателя, счётчиков и пустых состояний.
   * Полный `state.brands` остаётся под рукой — по нему живут карточка архивного
   * бренда и вкладка «Архив», — но рабочая поверхность спрашивает `liveBrands`,
   * иначе фильтр приходится повторять на каждом экране, и он разъезжается.
   */
  liveBrands: Brand[];
  /** Живые бренды выбранного пространства; без выбора — все живые. */
  visibleBrands: Brand[];
  /** Архив того же пространства: его показывает вкладка списка брендов. */
  archivedBrands: Brand[];
  /**
   * Сколько брендов ЛЮБОГО пространства лежит в архиве.
   *
   * Спрашивает экран «Клиенты»: `archivedBrands` выше — про выбранное
   * пространство, а карточек на экране столько, сколько у агентства клиентов,
   * и выбрано из них одно. Полный список остаётся здесь же, внутри области:
   * рабочая поверхность его не читает (`check-brand-archive.mjs`).
   */
  archivedCountIn: (workspaceId: string) => number;
  selectWorkspace: (id: string | null) => void;
  selectBrand: (id: string | null) => void;
  /**
   * Выбранное пространство ПРЯМО СЕЙЧАС, а не на момент отрисовки читателя.
   *
   * `workspaceId` выше — снимок: обработчик, замкнувшийся на него при нажатии,
   * увидит старое значение и через секунду, и после того, как экран сменился.
   * Тем, кто спрашивает выбор ПОСЛЕ ответа сервера, снимок врёт: за время
   * запроса человек успевает переключиться, а экран, с которого начали, —
   * размонтироваться. Область живёт выше всех экранов, поэтому свежий ответ
   * знает она.
   */
  currentWorkspaceId: () => string | null;
  /** Выбранный бренд ПРЯМО СЕЙЧАС — по той же причине, что и пространство. */
  currentBrandId: () => string | null;
  reload: () => void;
}

/**
 * Через сколько вернувшаяся вкладка считается отставшей.
 *
 * Область — это переключатель, счётчики и список брендов; всё это меняется в
 * соседней вкладке и в соседнем кресле. Пять секунд выбраны так, чтобы обычное
 * «переключился и вернулся» уже обновляло данные, но щелчок по вкладке и назад
 * не превращался в очередь запросов.
 */
const STALE_AFTER_MS = 5000;

const Context = createContext<ScopeApi | null>(null);

/** Общий пустой список: см. пояснение у места использования. */
const NOTHING: never[] = [];

/**
 * Живой бренд — тот, по которому работа продолжается.
 *
 * Архивный ушёл из продукта целиком: замеров по нему нет, квоту он освободил,
 * имя отдал. Оставлять его в переключателе — предлагать выбрать пустоту: экран
 * замера покажет по нему ноль строк, а «Запустить замер» получит отказ сервера.
 */
export function isLiveBrand(brand: Brand): boolean {
  return brand.status !== 'archived';
}

export function liveOnly(brands: readonly Brand[]): Brand[] {
  return brands.filter(isLiveBrand);
}

/**
 * Сколько брендов пространства лежит в архиве.
 *
 * Спрашивает экран «Клиенты», и вопрос у него не праздный. В сводке клиента
 * (`GET /agency/clients`) лежат только живые бренды — сервер кладёт туда ровно
 * их, — поэтому клиент, убравший из работы всё, выглядит там пустым. В
 * переключателе наверху его тоже нет: пространство без живых брендов не даёт
 * группы (`ContextSwitcher`). А вкладка «Архив» показывает архив ВЫБРАННОГО
 * пространства, и кнопка «Удалить» архивного бренда живёт только в ней.
 *
 * Считается по тому же полному списку области и той же меркой живости, что и
 * `archivedBrands`: два ответа на вопрос «это архив?» разъедутся на первой же
 * правке, и карточка клиента обещала бы архив там, где вкладка его не покажет.
 *
 * Список области — первая страница `GET /brands`: бренды дальше неё сюда не
 * попадут ровно так же, как не попадают в переключатель.
 */
export function archivedCountOf(brands: readonly Brand[], workspaceId: string): number {
  return brands.filter((b) => b.workspace_id === workspaceId && !isLiveBrand(b)).length;
}

/**
 * Что выбрано сразу после загрузки области.
 *
 * Сохранённый выбор принимается, только если он ЖИВОЙ, а не просто существует.
 * Бренд, заархивированный в соседней вкладке, лежит в `sessionStorage` ровно
 * так же, как рабочий, и проверка «нашёлся в списке» его пропускала — кабинет
 * открывался на бренде, по которому нет ни чисел, ни замеров.
 *
 * Единственный живой бренд выбирается сам. Спрашивать «какой бренд смотрим»
 * там, где вариант ровно один, — это экран, который каждый раз просит
 * подтвердить очевидное, и он же прячет числа за лишним щелчком.
 */
export function initialScope(
  workspaces: readonly Workspace[],
  brands: readonly Brand[],
  stored: { workspace?: string; brand?: string },
): { workspace: string | null; brand: string | null } {
  const live = liveOnly(brands);
  const workspace = workspaces.find((w) => w.id === stored.workspace)?.id ?? null;
  const brand = live.find((b) => b.id === stored.brand) ?? null;

  // Пространство берётся у бренда, а не остаётся сохранённым: «бренд одного
  // клиента внутри пространства другого» — область, которой не бывает, и
  // списки в ней противоречат переключателю.
  if (brand && (!workspace || brand.workspace_id === workspace)) {
    return { workspace: brand.workspace_id, brand: brand.id };
  }

  // Единственный живой бренд выбирается сам — но не поверх пространства,
  // выбранного явно и без бренда: у агентства, только что заведшего пустого
  // клиента, область уже стоит на нём, и автовыбор молча возвращал бы её к
  // чужому бренду — онбординг предлагал бы завести бренд клиента не туда.
  // Если же сохранённый выбор указывал на бренд, а тот умер (архив, чужая
  // организация), переезд к единственному живому — ровно то, чего ждут.
  // `!stored.brand`, а не сравнение с undefined: хранилище переживает старые
  // версии кабинета и ручную правку, пустая строка обязана значить «бренда нет».
  const only = live.length === 1 ? live[0] : null;
  const emptyPlaceChosen = workspace !== null && !stored.brand;
  if (only && (!emptyPlaceChosen || only.workspace_id === workspace)) {
    return { workspace: only.workspace_id, brand: only.id };
  }

  return { workspace, brand: null };
}

/**
 * Чем станет выбор при попытке открыть бренд; `null` — попытка отклонена.
 *
 * Архивный не выбирается ниоткуда: ни из переключателя, ни из карточки, ни из
 * сохранённого выбора. Отказ молчаливый намеренно — предлагать этот выбор
 * попросту нечему, кнопки на архивный бренд не ведут.
 *
 * Обе функции выбора — эта и `workspaceSelection` — берут ПОЛНЫЙ список и
 * решают про живость сами. Правило одно: «живость решается внутри, а не выбором
 * аргумента снаружи». Иначе однажды кто-то передаст сюда живой список, чтобы
 * «упростить», и архивный бренд станет выбираемым молча — отклонять будет
 * нечего, его просто не найдут.
 */
export function brandSelection(
  brands: readonly Brand[],
  id: string | null,
  workspaceId: string | null,
): { brand: string | null; workspace: string | null } | null {
  if (id === null) return { brand: null, workspace: workspaceId };

  const brand = brands.find((b) => b.id === id) ?? null;
  if (brand && !isLiveBrand(brand)) return null;

  // Выбор бренда доопределяет пространство: путь в переключателе всегда
  // полный, «бренд без пространства» показывать нечестно.
  return { brand: id, workspace: brand ? brand.workspace_id : workspaceId };
}

/**
 * Чем станет выбор при смене пространства.
 *
 * Бренд остаётся, только если он живой и принадлежит новому пространству: в
 * чужой области он бессмысленен, а архивный бессмысленен везде. Список тот же
 * полный, что и у `brandSelection`, — см. пояснение там.
 */
export function workspaceSelection(
  brands: readonly Brand[],
  id: string | null,
  brandId: string | null,
): { brand: string | null; workspace: string | null } {
  const brand = brands.find((b) => b.id === brandId) ?? null;
  const keep = brand && isLiveBrand(brand) && (!id || brand.workspace_id === id);
  return { brand: keep ? brand.id : null, workspace: id };
}

function storageKey(organizationId: string): string {
  return `seeno.scope.${organizationId}`;
}

function readStored(organizationId: string): { workspace?: string; brand?: string } {
  try {
    const raw = sessionStorage.getItem(storageKey(organizationId));
    return raw ? (JSON.parse(raw) as { workspace?: string; brand?: string }) : {};
  } catch {
    // Приватный режим, переполненное хранилище, испорченный JSON — выбор
    // области не настолько важен, чтобы ронять из-за него кабинет.
    return {};
  }
}

function writeStored(
  organizationId: string,
  value: { workspace?: string; brand?: string },
) {
  try {
    sessionStorage.setItem(storageKey(organizationId), JSON.stringify(value));
  } catch {
    // см. выше
  }
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  /**
   * Активная организация — из объекта организации, а если его нет, то из членства.
   *
   * Здесь стояло только `session.organization?.id ?? ''`, и это ломало тариф
   * «Партнёр» целиком. Сервер для роли `agency_client` намеренно возвращает
   * `organization: null` («он не должен знать, у кого обслуживается»), поэтому
   * идентификатор оказывался пустым, эффект ниже выходил по `if
   * (!organizationId) return`, и пространства с брендами оставались пустыми
   * НАВСЕГДА. API при этом отдавал клиенту его данные исправно: `GET /brands` —
   * бренд, `GET /portfolio` — карточку, `GET /workspaces` — пространство.
   * Агентство приводило клиента в панель, где нет ничего.
   *
   * Членство приходит всем ролям и несёт идентификатор организации — им и
   * пользуемся. Изоляции это не ослабляет: арендатор всё равно живёт в токене,
   * а идентификатор здесь нужен только чтобы разделить сохранённый выбор
   * области между организациями.
   */
  const organizationId =
    session.organization?.id ?? session.membership?.organization_id ?? '';

  // Провайдер пересоздаётся на смене организации (`key` в оболочке), поэтому
  // начальное значение считается один раз и всегда верно: без организации
  // выбирать не из чего и грузить нечего.
  const [state, setState] = useState<ScopeState>(() =>
    organizationId ? { kind: 'loading' } : { kind: 'ready', workspaces: [], brands: [] },
  );
  const [attempt, setAttempt] = useState(0);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);

  /* Свежий выбор для тех, кто спрашивает его после ответа сервера: обработчик
     удаления клиента живёт дольше своего экрана, и снимок, взятый при нажатии,
     к тому времени успевает устареть дважды — человек переключается сам, а
     экран, с которого начали, размонтируется. */
  const selected = useRef<string | null>(null);
  useEffect(() => {
    selected.current = workspaceId;
  }, [workspaceId]);
  const currentWorkspaceId = useCallback(() => selected.current, []);

  /* То же самое про бренд, и по той же причине. Удаление спрашивает выбор
     ПОСЛЕ ответа сервера: за это время человек успевает переключиться сам —
     и в обе стороны. Уйдя с удаляемого бренда, он не должен потерять новый
     выбор; перейдя на него, обязан с него съехать. Снимок на момент щелчка
     отвечает на оба вопроса неверно. */
  const chosen = useRef<string | null>(null);
  useEffect(() => {
    chosen.current = brandId;
  }, [brandId]);
  const currentBrandId = useCallback(() => chosen.current, []);

  /* Когда область читалась в последний раз и молча ли её перечитывают.

     Перезагрузок две, и они про разное. `reload()` — это ответ на действие
     человека: он сбрасывает экраны в ожидание, и скелет здесь уместен, потому
     что человек только что нажал. Возвращение во вкладку действием не является:
     тот же сброс мигал бы скелетом всего кабинета на каждом переключении окон,
     а сорвавшийся фоновый запрос заменял бы работающий экран красной плашкой.
     Поэтому фоновое перечитывание идёт молча — данные обновляются на месте, а
     отказ оставляет то, что уже показано. */
  const quiet = useRef(false);
  const readAt = useRef(0);

  const reload = useCallback(() => {
    quiet.current = false;
    // Сброс в ожидание делает обработчик, а не эффект: иначе повтор загрузки
    // становится каскадом перерисовок на каждом её запуске.
    setState({ kind: 'loading' });
    setAttempt((value) => value + 1);
  }, []);

  const refresh = useCallback(() => {
    quiet.current = true;
    setAttempt((value) => value + 1);
  }, []);

  /**
   * Вкладка, пролежавшая в фоне, показывает вчерашний кабинет.
   *
   * Выбор области живёт в `sessionStorage` по вкладке и проверяется только при
   * загрузке. Бренд, удалённый в соседней вкладке, здесь остался бы и в
   * переключателе, и выбранным — то есть каждый экран спрашивал бы данные
   * бренда, которого для API больше нет, и человек получал бы «не найдено» там,
   * где ничего не ломал. Тем же лечится устаревшая квота и устаревший список
   * клиентов.
   */
  useEffect(() => {
    if (!organizationId) return;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      /* Обновляется только то, что УЖЕ показано.
         Без этого условия возвращение во вкладку посреди первой загрузки
         обрывало бы её (эффект перезапускается и рвёт свой `AbortController`)
         и запускало вместо неё молчаливую. А молчаливая, сорвавшись, ничего не
         показывает — экран остался бы в скелете навсегда, без ошибки и без
         кнопки «Повторить». Медленная сеть попадала бы в это каждый раз. */
      if (state.kind !== 'ready') return;
      if (Date.now() - readAt.current < STALE_AFTER_MS) return;
      refresh();
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [organizationId, refresh, state.kind]);

  useEffect(() => {
    if (!organizationId) return;

    /* Решение «молча или со скелетом» принимает тот, кто попросил перезагрузку,
       и здесь оно только читается. Снимать флаг внутри эффекта нельзя: в
       разработке `StrictMode` выполняет эффект дважды, второй проход увидел бы
       уже снятый флаг и объявил бы фоновую неудачу ошибкой экрана. Следующий
       вызов `reload` или `refresh` перепишет его сам. */
    const silent = quiet.current;

    /* Отсчёт давности идёт от НАЧАЛА чтения, а не от его конца: иначе медленный
       запрос считается «давним» всё время, пока он идёт, и каждое возвращение
       во вкладку рвёт его ради нового. */
    readAt.current = Date.now();

    const abort = new AbortController();

    // Оба списка курсорные. Оболочке нужен не весь каталог, а то, что помещается
    // в переключатель: первая страница и есть верхняя граница разумного меню.
    Promise.all([
      api.get<Page<Workspace>>('/workspaces', {
        signal: abort.signal,
        query: { limit: 100, status: 'active' },
      }),
      api.get<Page<Brand>>('/brands', {
        signal: abort.signal,
        query: { limit: 200 },
      }),
    ])
      .then(([workspaces, brands]) => {
        readAt.current = Date.now();
        // Восстановление выбора и автовыбор единственного — в `initialScope`:
        // правило одно и то же на первой загрузке, на повторе после архивации
        // и в приёмке, а разложенное здесь по месту оно проверяется только
        // глазами.
        const picked = initialScope(
          workspaces.items,
          brands.items,
          readStored(organizationId),
        );

        setWorkspaceId(picked.workspace);
        setBrandId(picked.brand);
        setState({ kind: 'ready', workspaces: workspaces.items, brands: brands.items });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Фоновое перечитывание проваливается молча: человек ничего не нажимал,
        // и подменять работающий экран красной плашкой из-за сорвавшегося
        // запроса — значит наказывать его за то, что он вернулся во вкладку.
        // Следующее возвращение попробует снова.
        readAt.current = Date.now();
        if (silent) return;
        setState({ kind: 'failed', error });
      });

    return () => abort.abort();
  }, [organizationId, attempt]);

  // Пустой список — общий на все перерисовки: свежий `[]` каждый раз даёт новую
  // ссылку и обнуляет все useMemo ниже.
  const brands = state.kind === 'ready' ? state.brands : NOTHING;
  const workspaces = state.kind === 'ready' ? state.workspaces : NOTHING;

  const liveBrands = useMemo(() => liveOnly(brands), [brands]);

  const visibleBrands = useMemo(
    () =>
      workspaceId ? liveBrands.filter((b) => b.workspace_id === workspaceId) : liveBrands,
    [liveBrands, workspaceId],
  );

  // Архив того же пространства. Он не исчезает бесследно — исчезает только из
  // рабочих поверхностей, а вкладка «Архив» показывает его тем же списком.
  const archivedBrands = useMemo(
    () =>
      brands.filter(
        (b) => !isLiveBrand(b) && (!workspaceId || b.workspace_id === workspaceId),
      ),
    [brands, workspaceId],
  );

  const archivedCountIn = useCallback(
    (workspaceId: string) => archivedCountOf(brands, workspaceId),
    [brands],
  );

  const selectWorkspace = useCallback(
    (id: string | null) => {
      // Бренд другого пространства в новой области бессмысленен: сбрасываем,
      // а не оставляем «висеть» вне выбранного пути. Решение — в
      // `workspaceSelection`, парной к `brandSelection`.
      const next = workspaceSelection(brands, id, brandId);

      setWorkspaceId(next.workspace);
      setBrandId(next.brand);
      writeStored(organizationId, {
        workspace: next.workspace ?? undefined,
        brand: next.brand ?? undefined,
      });
    },
    [brands, brandId, organizationId],
  );

  const selectBrand = useCallback(
    (id: string | null) => {
      // Решение целиком в `brandSelection`: оно же стоит между сохранённым
      // выбором и экраном, и разъехаться этим двум нельзя.
      const next = brandSelection(brands, id, workspaceId);
      if (!next) return;

      setBrandId(next.brand);
      setWorkspaceId(next.workspace);
      writeStored(organizationId, {
        workspace: next.workspace ?? undefined,
        brand: next.brand ?? undefined,
      });
    },
    [brands, organizationId, workspaceId],
  );

  const value = useMemo<ScopeApi>(
    () => ({
      state,
      workspaceId,
      brandId,
      workspace: workspaces.find((w) => w.id === workspaceId) ?? null,
      // Выбранный бренд ищется среди живых: архивный сюда не попадёт даже из
      // сохранённого выбора прошлой вкладки.
      brand: liveBrands.find((b) => b.id === brandId) ?? null,
      liveBrands,
      visibleBrands,
      archivedBrands,
      archivedCountIn,
      selectWorkspace,
      selectBrand,
      currentWorkspaceId,
      currentBrandId,
      reload,
    }),
    [
      state,
      workspaceId,
      brandId,
      workspaces,
      liveBrands,
      visibleBrands,
      archivedBrands,
      archivedCountIn,
      selectWorkspace,
      selectBrand,
      currentWorkspaceId,
      currentBrandId,
      reload,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useScope(): ScopeApi {
  const scope = useContext(Context);
  if (!scope) throw new Error('ScopeProvider не найден выше по дереву');
  return scope;
}
